// Marketing collateral config for chemical products (BoneDry, Pinkwater).
// Data-driven: each key powers the reusable collateral page components.
import { IMAGES } from "./data";

export const COLLATERAL = {
  bonedry: {
    key: "bonedry",
    slug: "bonedry",
    name: "BoneDry",
    category: "Absorbents & Spill Control",
    image: IMAGES.bonedry,
    tagline: "High-performance absorbent that cuts cost, weight, and cleanup time per incident.",

    // ---- Value Proposition Matrix ----
    matrixHeadline: "Clean Up Faster. Haul Away Less. Spend Less Per Incident.",
    matrixSub: "One absorbent engineered on three pillars — Encapsulation. Light Weight. Rapid Response.",
    pillars: [
      ["Encapsulation", "Locks up hydrocarbons — reduces leaching & rework."],
      ["Light Weight", "Up to 5× lighter than clay — less to buy, lift & haul."],
      ["Rapid Response", "Sweep-and-go cleanup, non-slip, residue-free surface."],
    ],
    personas: [
      { role: "EHS / Environmental Manager", segment: "Industrial & Energy",
        pain: "**Leaching & rework** from cheap absorbents. **Reportable releases** risk.",
        tag: "Encapsulation",
        solution: "**Encapsulates hydrocarbons** — cuts leaching and reduces secondary contamination.",
        outcomes: ["Lower release risk", "Cleaner closeouts"] },
      { role: "Operations / Field Superintendent", segment: "Heavy Civil & Energy",
        pain: "Spills **stall the job.** Slow cleanup, slick surfaces, **downtime.**",
        tag: "Rapid Response",
        solution: "**Sweep-and-go** cleanup leaves a **non-slip, residue-free** surface — crews back to work fast.",
        outcomes: ["Less downtime", "Safer footing"] },
      { role: "Maintenance / Facilities Manager", segment: "Fleet & Shops",
        pain: "**Clay everywhere,** heavy bags, constant **re-stocking.**",
        tag: "Light Weight",
        solution: "**Up to 5× lighter** — uses less material and stores compact for readiness.",
        outcomes: ["Less material used", "Simple readiness"] },
      { role: "Procurement", segment: "Enterprise & Multi-Site",
        pain: "**Disposal weight & hauling** costs balloon. Buying **volume of clay.**",
        tag: "Encapsulation",
        solution: "**~60% less disposal weight** and less volume used drops **total cost per incident.**",
        outcomes: ["~50% lower cost/incident", "Fewer POs"] },
      { role: "Compliance Officer", segment: "Regulated Facilities",
        pain: "**Silica-hazard** clay dust. **Disposal documentation** headaches.",
        tag: "Encapsulation",
        solution: "**Non-toxic, silica-free** sorbent that supports **SPCC / spill-readiness** programs.",
        outcomes: ["Safer handling", "Program support"] },
    ],

    // ---- ROI / Cost-Benefit ----
    roiHeadline: "The Real Cost of a Spill Isn't the Bag.",
    roiSub: "It's the volume used, the labor, and the disposal weight you haul away. BoneDry cuts all three.",
    roiChips: [["~50%", "lower cost / incident"], ["~60%", "less disposal weight"]],

    // ---- Technical Data Sheet ----
    datasheet: {
      model: "BoneDry All / HC / H2O / SC / Crime",
      subtitle: "High-Performance Encapsulating Absorbent",
      headline: [
        { v: "up to 5×", l: "lighter than clay" },
        { v: "Non-leach", l: "encapsulates hydrocarbons" },
        { v: "Silica-free", l: "no crystalline silica" },
        { v: "Reusable*", l: "sweep & re-apply" },
      ],
      groups: [
        { title: "Physical & Material", rows: [["Form", "Free-flowing granular sorbent"], ["Base", "Organic / cellulosic media"], ["Bulk density", "Ultralight vs mineral clay"], ["Color", "Natural / light"], ["Packaging", "Jugs, 3 lb bags, 2 ft³ bags, drums, super sacks"]] },
        { title: "Performance", rows: [["Absorbs", "Oil, fuel, hydrocarbons & fluids"], ["Water (HC grade)", "Repels water — absorbs oil only"], ["Encapsulation", "Locks hydrocarbons to reduce leaching"], ["Surface finish", "Non-slip, residue-free"], ["Recovery", "Sweep / shovel & bag"]] },
        { title: "Application", rows: [["Surfaces", "Hard floors, ground, water (HC)"], ["Use", "Maintenance, fueling, rapid response"], ["Handling", "Non-abrasive, equipment-safe"], ["Kits", "Socks, booms, portable & full spill kits"]] },
        { title: "Safety & Handling", rows: [["Toxicity", "Non-toxic, non-carcinogenic"], ["Silica", "No free crystalline silica"], ["Storage", "Dry, ambient"], ["SDS", "Available on request"]] },
      ],
      env: [
        "Encapsulates hydrocarbons to reduce leaching into soils & water",
        "Lighter loads reduce disposal weight & hauling",
        "Non-toxic, silica-free — safer crew handling",
        "Supports SPCC & spill-readiness programs",
      ],
      skus: [
        ["BoneDry All", "Oil & water-based on hard surfaces", "Mixed spill types"],
        ["BoneDry HC", "Absorbs oil, not water", "Wet environments"],
        ["BoneDry H2O", "Solidifies water-based cuttings", "Drilling / HDD"],
        ["BoneDry SC", "Spent caustic & odor control", "Refining / process"],
        ["BoneDry Crime", "Biofluid cleanup", "Facilities / transit"],
        ["Containment", "Booms, socks & kits", "Readiness / water-adjacent"],
      ],
      note: "*Reuse where saturation & material condition permit. Confirm disposal per federal/state/local regulations.",
    },

    // ---- Battlecard ----
    battlecard: {
      headline: "BoneDry vs. the Old Way.",
      cols: ["BoneDry", "Clay absorbent", "Pads / socks", "Vac / sweep"],
      rows: [
        ["Weight to handle", ["Ultralight", "Heavy", "Light", "Equipment"], [1, 3, 1, 2]],
        ["Absorbency / volume", ["High — less used", "Low — more used", "Moderate", "n/a"], [1, 3, 2, 2]],
        ["Repels water (HC)", ["Yes (HC grade)", "No", "Some", "No"], [1, 3, 2, 3]],
        ["Disposal weight", ["Low", "Very high", "Moderate", "High"], [1, 3, 2, 3]],
        ["Leaching", ["Encapsulates", "Can leach", "Varies", "n/a"], [1, 3, 2, 2]],
        ["Silica hazard", ["Silica-free", "Often present", "None", "n/a"], [1, 3, 1, 1]],
        ["Cost per incident", ["Lowest", "High (volume)", "Moderate", "High (equip)"], [1, 3, 2, 3]],
      ],
    },

    // ---- Case Study ----
    caseStudy: {
      headline: "6-Site Fleet Program: 50% Lower Cost Per Spill.",
      sector: "Fleet Maintenance & Fueling · Multi-site industrial operator",
      challenge: ["**Heavy clay** used across 6 yards.", "**High disposal tonnage** & hauling.", "**Slip risk** on shop floors."],
      deployment: ["Standardized on **BoneDry HC & All.**", "Stocked **kits & socks** for readiness.", "Trained crews on **sweep-and-go.**"],
      outcome: ["**~60% less** material used.", "**Lighter** disposal loads.", "**Non-slip** floors, fewer incidents."],
      results: [{ v: "~50%", l: "Lower cost per incident" }, { v: "~60%", l: "Less disposal weight" }, { v: "5×", l: "Lighter than prior clay" }, { v: "6", l: "Sites standardized" }],
      quote: "We use less, lift less, and haul away less. The floors are safer, too.",
    },

    // ---- Compliance / Environmental ----
    compliance: {
      headline: "Contain the Hydrocarbon. Cut the Liability.",
      sub: "BoneDry supports your environmental and spill-readiness programs — encapsulating hydrocarbons, reducing disposal weight, and eliminating silica-dust exposure.",
      chips: [["Non-leach", "encapsulation"], ["Silica-free", "safer handling"], ["Recyclable*", "lighter disposal"]],
      pillars: [
        { title: "Contamination Control", rows: ["**Encapsulates hydrocarbons.**", "Reduces **leaching** into soils & water.", "Supports **cleaner closeouts.**", "Lowers **secondary contamination.**"] },
        { title: "Crew & Site Safety", rows: ["**Non-toxic**, non-carcinogenic.", "**No crystalline silica** dust.", "**Non-slip**, residue-free surface.", "Equipment-safe, **non-abrasive.**"] },
        { title: "Waste & Readiness", rows: ["**Lighter** disposal loads.", "**Less volume** used per incident.", "Supports **SPCC / readiness** plans.", "Documented **SDS** available."] },
      ],
      why: [["Compliance:", "supports SPCC & spill-readiness documentation."], ["Liability:", "encapsulation reduces leaching exposure."], ["Safety:", "silica-free handling protects crews."]],
    },

    // ---- Interactive ROI Calculator ----
    calculator: {
      headline: "BoneDry Spill-Cost Calculator",
      sub: "Model your true cost per spill — material, labor, and disposal — against conventional clay.",
      fields: [
        { key: "sites", label: "Number of sites", def: 6 },
        { key: "perSite", label: "Spill incidents / site / year", def: 20 },
        { key: "cost", label: "Current cost / incident ($)", def: 400 },
      ],
      compute: (v) => {
        const incidents = Math.max(0, v.sites) * Math.max(0, v.perSite);
        const current = incidents * Math.max(0, v.cost);
        const product = current * 0.5;
        const save = current - product;
        return {
          cards: [
            { v: "50%", l: "Lower cost per incident", flame: true },
            { v: "62%", l: "Less absorbent volume" },
            { v: "60%", l: "Less disposal weight" },
            { v: "$" + Math.round(save).toLocaleString(), l: "Annual savings", flame: true },
          ],
          box: { aLabel: "Clay program spend", aVal: current, bLabel: "BoneDry spend", bVal: product, cLabel: "Annual savings", cVal: save },
          summary: `BONEDRY SPILL-COST ANALYSIS\n${v.sites} sites × ${v.perSite} incidents = ${incidents} incidents/yr\nCurrent cost/incident: $${v.cost}\nClay spend: $${Math.round(current).toLocaleString()} vs BoneDry: $${Math.round(product).toLocaleString()}\nANNUAL SAVINGS: $${Math.round(save).toLocaleString()} (~50% lower cost/incident)`,
        };
      },
    },
    sectors: ["Oil & Gas", "Heavy / Civil Construction", "Fire / Hazmat", "Industrial Facilities", "Municipal"],
    roles: ["EHS / Environmental", "Superintendent", "Maintenance / Facilities", "Procurement", "Compliance"],
  },

  pinkwater: {
    key: "pinkwater",
    slug: "pinkwater",
    name: "Pinkwater",
    category: "Water Treatment & Management",
    image: IMAGES.pinkwater,
    tagline: "On-site water treatment that turns problem water into compliant water.",

    matrixHeadline: "Treat It On Site. Haul Away Less. Discharge with Confidence.",
    matrixSub: "One treatment engineered on three pillars — Clarification. Compliance. Cost Reduction.",
    pillars: [
      ["Clarification", "Rapidly clears turbid, contaminated jobsite water."],
      ["Compliance", "Supports discharge requirements & permit conditions."],
      ["Cost Reduction", "Slashes hauling and disposal volume & spend."],
    ],
    personas: [
      { role: "EHS / Environmental Manager", segment: "Construction & Energy",
        pain: "**Turbid discharge** risk. **Permit exceedances** and stop-work.",
        tag: "Compliance",
        solution: "**Clarifies & conditions** water on site to support **discharge compliance.**",
        outcomes: ["Compliant discharge", "Fewer stop-works"] },
      { role: "Operations / Field Superintendent", segment: "Pipeline & Civil",
        pain: "**Dewatering delays.** Waiting on **water trucks** and tank space.",
        tag: "Clarification",
        solution: "**Rapid on-site clarification** keeps dewatering moving — **no waiting on haul-off.**",
        outcomes: ["Less downtime", "Keep the schedule"] },
      { role: "Maintenance / Facilities Manager", segment: "Industrial Sites",
        pain: "**Wash-water & sediment** piling up. Tank & basin **management.**",
        tag: "Clarification",
        solution: "Treats **wash-water & sediment** for reuse or compliant discharge.",
        outcomes: ["Simpler management", "Less tankage"] },
      { role: "Procurement", segment: "Enterprise Contractors",
        pain: "**Hauling & disposal** dominate the water budget.",
        tag: "Cost Reduction",
        solution: "On-site treatment cuts **hauled volume up to ~70%** — **~60% lower** total water cost.",
        outcomes: ["~60% lower water cost", "Fewer haul contracts"] },
      { role: "Compliance Officer", segment: "Regulated / Permitted Sites",
        pain: "**SWPPP & discharge** documentation. **Turbidity** limits.",
        tag: "Compliance",
        solution: "Supports **SWPPP** programs and **turbidity reduction** for permitted discharge.",
        outcomes: ["Permit support", "Documented results"] },
    ],

    roiHeadline: "Stop Paying to Haul Water. Treat It.",
    roiSub: "Every gallon you clarify on site is a gallon you don't truck away. Pinkwater cuts hauling, delays, and total water cost.",
    roiChips: [["~60%", "lower water mgmt cost"], ["~70%", "less water hauled"]],

    datasheet: {
      model: "BioSolve Pinkwater",
      subtitle: "Jobsite Water Clarification & Conditioning Treatment",
      headline: [
        { v: "Rapid", l: "clarification" },
        { v: "Biodegradable", l: "formulation" },
        { v: "Concentrated", l: "low dose rate" },
        { v: "Scalable", l: "jug to tote" },
      ],
      groups: [
        { title: "Physical & Chemical", rows: [["Form", "Liquid concentrate"], ["Appearance", "Pink / rose liquid"], ["Solubility", "Fully water-miscible"], ["pH (in use)", "Near-neutral working range"], ["Specific gravity", "~1.0–1.1"]] },
        { title: "Performance", rows: [["Clarifies", "Turbid, sediment-laden water"], ["Conditions", "Supports discharge parameters"], ["Turbidity", "Rapid reduction"], ["Dose", "Low, volume-based dosing"], ["Effective on", "Sediments, fines & residues"]] },
        { title: "Application", rows: [["Use", "Dewatering, hydro-test, wash-water"], ["Method", "Metered / batch dosing"], ["Equipment", "Tanks, weirs, frac tanks, basins"], ["Scale", "Spot treatment to bulk programs"]] },
        { title: "Environmental & Handling", rows: [["Biodegradable", "Yes"], ["Handling", "Standard PPE"], ["Storage", "Ambient, avoid freezing"], ["SDS", "Available on request"]] },
      ],
      env: [
        "Reduces hauled water volume & disposal footprint",
        "Biodegradable treatment chemistry",
        "Supports discharge compliance & SWPPP programs",
        "Turbidity reduction for permitted release",
      ],
      skus: [
        ["1 Gallon Jug", "Spot treatment & trials", "Small tasks"],
        ["5 Gallon Pail", "Recurring small-mid volume", "Regular crews"],
        ["55 Gallon Drum", "Sustained treatment", "Active jobsites"],
        ["275 Gallon Tote", "High-volume continuous", "Bulk programs"],
      ],
      note: "Dosing rates & discharge suitability confirmed per water profile & local permit requirements.",
    },

    battlecard: {
      headline: "Pinkwater vs. Hauling It Away.",
      cols: ["Pinkwater", "Haul-off & disposal", "Settling / floc", "Mobile filtration"],
      rows: [
        ["On-site treatment", ["Yes", "No", "Partial", "Yes"], [1, 3, 2, 1]],
        ["Hauled volume", ["Minimal", "All of it", "Most", "Some"], [1, 3, 3, 2]],
        ["Turnaround", ["Rapid", "Truck schedule", "Slow (time)", "Setup-heavy"], [1, 3, 3, 2]],
        ["Compliance support", ["Discharge-focused", "Offsite only", "Partial", "Yes"], [1, 2, 2, 1]],
        ["Labor", ["Low (dosing)", "Coordination", "Monitoring", "Operator"], [1, 2, 2, 3]],
        ["Equipment cost", ["Low", "n/a (trucking)", "Low", "High rental"], [1, 2, 1, 3]],
        ["Cost per gallon", ["Lowest", "High (trucking)", "Moderate", "High"], [1, 3, 2, 3]],
      ],
    },

    caseStudy: {
      headline: "Pipeline Hydro-Test: 500,000 Gallons Treated On Site.",
      sector: "Pipeline Construction · Hydrostatic testing · Permitted discharge",
      challenge: ["**Turbid test water** exceeded limits.", "**Hauling** was slow & costly.", "**Schedule** at risk waiting on trucks."],
      deployment: ["**Metered Pinkwater** dosing at the weir.", "Treated in **frac tanks** on site.", "Verified **turbidity** before release."],
      outcome: ["**Compliant** on-site discharge.", "**~70% less** water hauled.", "**No schedule** slip on dewatering."],
      results: [{ v: "500K", l: "Gallons treated on site" }, { v: "~60%", l: "Lower water mgmt cost" }, { v: "~70%", l: "Less water hauled" }, { v: "0", l: "Discharge exceedances" }],
      quote: "We treated it at the weir and discharged on spec — instead of trucking it for weeks.",
    },

    compliance: {
      headline: "Discharge on Spec. Truck Away Less.",
      sub: "Pinkwater supports discharge compliance and SWPPP programs while cutting the hauled volume that drives your water budget and footprint.",
      chips: [["Biodegradable", "chemistry"], ["Discharge", "compliance support"], ["~70%", "less hauled"]],
      pillars: [
        { title: "Discharge Compliance", rows: ["**Clarifies & conditions** on site.", "**Turbidity reduction** for release.", "Supports **permit conditions.**", "**Documented** treatment results."] },
        { title: "Environmental Footprint", rows: ["**Biodegradable** treatment.", "**Less hauled** water volume.", "Lower **disposal footprint.**", "Supports **SWPPP** programs."] },
        { title: "Water Management", rows: ["Handles **dewatering & hydro-test.**", "Treats **wash-water & sediment.**", "**Scalable** dosing.", "Reduces **tankage & trucking.**"] },
      ],
      why: [["Compliance:", "supports discharge & SWPPP documentation."], ["Footprint:", "biodegradable, less hauled volume."], ["Cost:", "on-site treatment beats trucking."]],
    },

    calculator: {
      headline: "Pinkwater Water-Cost Calculator",
      sub: "Model on-site treatment against haul-off & disposal per gallon.",
      fields: [
        { key: "gallons", label: "Gallons treated / year", def: 500000, step: 1000 },
        { key: "haul", label: "Haul-off cost / gallon ($)", def: 0.45, step: 0.01 },
        { key: "treat", label: "Pinkwater cost / gallon ($)", def: 0.12, step: 0.01 },
      ],
      compute: (v) => {
        const g = Math.max(0, v.gallons);
        const haul = g * Math.max(0, v.haul);
        const treat = g * Math.max(0, v.treat);
        const save = haul - treat;
        const pct = v.haul > 0 ? Math.round((1 - v.treat / v.haul) * 100) : 0;
        return {
          cards: [
            { v: pct + "%", l: "Lower cost per gallon", flame: true },
            { v: "~70%", l: "Less water hauled" },
            { v: "Rapid", l: "On-site turnaround" },
            { v: "$" + Math.round(save).toLocaleString(), l: "Annual savings", flame: true },
          ],
          box: { aLabel: "Haul-off spend", aVal: haul, bLabel: "Pinkwater spend", bVal: treat, cLabel: "Annual savings", cVal: save },
          summary: `PINKWATER WATER-COST ANALYSIS\n${g.toLocaleString()} gal/yr\nHaul-off @ $${v.haul}/gal = $${Math.round(haul).toLocaleString()}\nPinkwater @ $${v.treat}/gal = $${Math.round(treat).toLocaleString()}\nANNUAL SAVINGS: $${Math.round(save).toLocaleString()} (${pct}% lower cost/gal)`,
        };
      },
    },
    sectors: ["Pipeline", "Heavy / Civil Construction", "Utility / T&D", "Industrial Facilities", "Municipal"],
    roles: ["EHS / Environmental", "Superintendent", "Maintenance / Facilities", "Procurement", "Compliance"],
  },
};

export const getCollateral = (key) => COLLATERAL[key];
